import Vue from 'vue';
import ICog from 'src/assets/cog.svg';
import { Prop, Component, Watch, Inject } from 'vue-property-decorator';
import type { EntityOfType } from '@cognitoforms/model.js';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import { getEntryId } from 'src/web-api/entry-views/entry-view-util';
import type LookupManager from 'src/framework/model/extensions/lookup-field/lookup-manager';
import { computeImageValue } from 'src/framework/model/extensions/lookup-field/lookup-image-utils';
import type { FormEventScope } from 'src/framework/eventing/form-event-scope';
import { FormEvents } from 'src/framework/public/events';
import { removeExtraSpace } from 'src/util/helpers';
import { EntryStatus } from './EntryStatus';
import { getAvatarSVG, hashAvatarSeed } from '@cognitoforms/utils/avatar';
import type FileService from 'src/web-api/file-service';

const LEADING_HYPHEN_PATTERN = /^\s*-\s*/;

type ChoiceOption = {
	value: string;
	displayValue: string;
	label: string;
	description?: string;
	thumbnailImage?: string;
	fullSizeImage?: string;
	svg?: string;
};

@Component({
	name: 'CLookup',
	components: { ICog },
	model: {
		prop: 'value',
		event: 'change'
	}
})
export default class Lookup extends Vue {
	@Prop()
	value: EntityOfType<FormEntry> | EntityOfType<FormEntry>[];

	@Prop()
	manager: LookupManager;

	@Prop({ default: false })
	readonly: boolean;

	@Inject('formEvents')
	formEvents: FormEventScope;

	@Prop()
	entryStatus: EntryStatus;

	@Prop()
	fileService: FileService;

	@Prop({ default: false })
	isCard: boolean;

	placeholderFocused = false;
	initializing = true;
	slot = { id: null, ids: [] };
	forgetInitialValue = false;
	missing = [];
	initialValue: EntityOfType<FormEntry> | EntityOfType<FormEntry>[];
	private cleanupTasks: (() => void)[] = [];
	private loadDataTask: Promise<EntityOfType<FormEntry> | EntityOfType<FormEntry>[]>;

	async created() {
		if (this.formEvents) {
			const resetEntryCallback = () => {
				this.initialValue = this.value;
				this.forgetInitialValue = false;
				const unwatch = this.$watch('manager.filteredIndexes', () => {
					// after the filteredIndexes change once, allow the initial value to be forgotten on subsequent changes
					this.forgetInitialValue = true;
					unwatch();
				});

				this.focusLookup();

				if (!this.initializing) {
					if (this.manager.initializing) {
						return this.manager.ready;
					}
					else
						return this.manager.initialize();
				}
			};
			this.formEvents.on(FormEvents.ResetEntry, resetEntryCallback);
			this.cleanupTasks.push(() => this.formEvents.off(FormEvents.ResetEntry, resetEntryCallback));
		}

		this.initialValue = this.value;

		await this.manager.ready;
		// allow computed/watchers to run before setting loading to false
		await this.$nextTick();
		this.initializing = false;
		this.forgetInitialValue = true;
		this.focusLookup();
	}

	beforeDestroy() {
		this.cleanupTasks.forEach(task => task());
	}

	// Updates focus after lookup loads
	focusLookup() {
		// Try to get the input and set it to focus after loading
		this.$nextTick(() => {
			// Base case in recursion
			if (this.$el.querySelector('input, select')) {
				if (this.placeholderFocused) {
					this.placeholderFocused = false;
					(this.$el.querySelector('input, select') as HTMLElement).focus();
				}
			}
			else if (!this.readonly)
				return setTimeout(() => this.focusLookup(), 100);
		});
	}

	handleBlur() {
		if (this.initializing)
			this.placeholderFocused = false;
	}

	handleFocus() {
		this.placeholderFocused = true;
	}

	get isList() {
		return Array.isArray(this.value);
	}

	get valueAsOptions() {
		const options = this.createOptionsFromEntries(this.value);

		if (this.isList && this.manager?.filteredIndexes) {
			options.sort((a, b) => {
				const aIndex = this.manager.filteredIndexes.find(idx => {
					const entryId = idx.EntityId;
					return entryId === a.value;
				});
				const bIndex = this.manager.filteredIndexes.find(idx => {
					const entryId = idx.EntityId;
					return entryId === b.value;
				});

				if (!aIndex || !bIndex) return 0;

				const ak = aIndex.SortKey;
				const bk = bIndex.SortKey;
				return ak === bk ? 0 : ak < bk ? -1 : 1;
			});
		}

		return options;
	}

	addImageUrls(option: ChoiceOption, imageId: string) {
		const fileObject = {
			Id: imageId,
			Name: '',
			ContentType: '',
			Size: 0,
			IsEncrypted: false,
			StorageUrl: ''
		};

		option.thumbnailImage = this.fileService.getThumbnailUrl(fileObject);
		option.fullSizeImage = this.fileService.getDownloadUrl(fileObject);
	}

	createAvatarSvg(imageValue: string) {
		const [initials, seedStr] = imageValue.split('|');
		const seedVal = parseInt(seedStr);
		const seed = Number.isNaN(seedVal) ? hashAvatarSeed(initials) : seedVal;
		return getAvatarSVG({ FirstName: initials.at(0) ?? '', LastName: initials.at(1) ?? '' }, seed);
	}

	private applyImageToOption(option: ChoiceOption, imageValue: string) {
		if (imageValue.startsWith('F-')) {
			this.addImageUrls(option, imageValue);
		}
		else {
			option.svg = this.createAvatarSvg(imageValue);
		}
	}

	createOptionsFromEntries(entries: EntityOfType<FormEntry> | EntityOfType<FormEntry>[]): ChoiceOption[] {
		return (Array.isArray(entries) ? entries : [entries])
			.filter(entry => !!entry)
			.map((entry: EntityOfType<FormEntry>) => {
				const displayValue = removeExtraSpace(entry.toString(this.manager.summaryFormat));
				const option = { displayValue: displayValue, value: entry.Id } as ChoiceOption;

				const index = this.manager.filteredIndexes.find(o => o.Id.split('|').at(2) === entry.Id)
					?? this.manager.indexes.find(o => o.Id.split('|').at(2) === entry.Id);

				// Add description for cards if it exists
				if (this.isCard && index && index.Description) {
					option.label = index.Summary ? removeExtraSpace(index.Summary) : displayValue;
					const cleanDescription = index.Description.replace(LEADING_HYPHEN_PATTERN, '');
					option.description = removeExtraSpace(cleanDescription);
				}

				const imageValue = index?.AdditionalValues?.find(v => v.FieldId === 'Image');
				if (imageValue) {
					this.applyImageToOption(option, imageValue.Value);
				}
				else if (this.manager.imageFieldPath) {
					// Fallback: compute image directly from entry when index doesn't have image data.
					// This handles entries that ARE in the EntrySet but filtered out by lookup filter,
					// which use real indexes (without AdditionalValues.Image) instead of synthetic indexes.
					const computedImage = computeImageValue(entry, this.manager.imageFieldPath, this.manager.nameFieldPath, this.manager.emailFieldPath);
					if (computedImage) {
						this.applyImageToOption(option, computedImage);
					}
				}

				return option;
			});
	}

	@Watch('manager.filteredIndexes')
	filterChanged() {
		if (this.forgetInitialValue)
			this.initialValue = null;
	}

	get choiceOptions(): ChoiceOption[] {
		// in readonly mode, just derive the "options" from the form entry, since only the selected options are relevant for readonly
		if (this.readonly)
			return this.valueAsOptions;
		else {
			const optionsFromIndex = this.manager.filteredIndexes.map(o => {
				const showDescription = (this.isCard || !this.readonly) && o.Description;
				const cleanDescription = this.isCard ? o.Description.replace(LEADING_HYPHEN_PATTERN, '') : o.Description;
				const option = {
					displayValue: removeExtraSpace((o.Summary + (showDescription ? ` ${o.Description}` : ''))),
					value: getEntryId(o),
					label: o.Summary ? removeExtraSpace(o.Summary) : '',
					description: showDescription ? removeExtraSpace(cleanDescription) : undefined
				} as ChoiceOption;

				const imageValue = o?.AdditionalValues?.find(v => v.FieldId === 'Image');
				if (imageValue) {
					this.applyImageToOption(option, imageValue.Value);
				}
				return option;
			});

			// if we're allowing the lookup to have a value that isn't in the index, ensure it has a corresponding choice option
			// The resulting array may not be in the correct order when editing
			if (this.manager.allowUnknownEntry) {
				const optionsValueSet = new Set(optionsFromIndex.map(o => o.value));
				this.missing = this.createOptionsFromEntries(this.initialValue)
					.filter(o => !optionsValueSet.has(o.value));
				return this.missing.concat(optionsFromIndex);
			}
			else
				return optionsFromIndex;
		}
	}

	private updateCounter = 0;
	@Watch('value', { immediate: true })
	async valueUpdated(value: EntityOfType<FormEntry> | EntityOfType<FormEntry>[]) {
		const updateCounter = ++this.updateCounter;
		// don't push the value change to the slot until the manager is initialized to ensure the options list is populated with
		// an option which corresponds to the lookup value
		if (this.manager.initializing)
			await this.manager.ready;

		if (this.updateCounter !== updateCounter)
			return;

		const optionIds = new Set(this.choiceOptions.map(o => o.value));
		if (this.isList) {
			const formEntryIds = (value as EntityOfType<FormEntry>[]).map(e => e.Id);
			if (this.initializing || this.manager.allowUnknownEntry)
				this.slot.ids = formEntryIds;
			else
				this.slot.ids = formEntryIds.filter(id => optionIds.has(id));
		}
		else if (value && 'Id' in value && (this.initializing || this.manager.allowUnknownEntry || optionIds.has(value.Id)))
			this.slot.id = (value as EntityOfType<FormEntry>).Id;
		else
			this.slot.id = null;
	}

	@Watch('slot.id')
	async valueUpdatedByUser(id) {
		if (this.isList)
			return;

		// Exit if the form is currently rendering (due to entry changing on the entries page)
		if (this.entryStatus === EntryStatus.Pending)
			return;

		if (!id) {
			this.loadDataTask = null;
			this.$emit('change', null);
		}
		else if (!this.value || (this.value as FormEntry).Id !== id) {
			const ignoreFilter = this.missing.some(o => o.value === id);
			const loadDataTask = this.loadDataTask = this.manager.getEntryById(id, ignoreFilter);
			const newValue = await loadDataTask;
			if (loadDataTask === this.loadDataTask)
				this.$emit('change', newValue);
		}
	}

	@Watch('slot.ids')
	async listValueUpdatedByUser(ids: string[]) {
		if (!this.isList)
			return;

		// Exit if the form is currently rendering (due to entry changing on the entries page)
		if (this.entryStatus === EntryStatus.Pending)
			return;

		const currentIds = ((this.value || []) as FormEntry[]).map(e => e.Id);
		if (this.isList && (ids.length !== currentIds.length || !ids.every(id => currentIds.includes(id)))) {
			const ignoreFilterFor = (id: string) => this.missing.some(o => o.value === id);
			const loadDataTask = this.loadDataTask = Promise.all(ids.map(id => this.manager.getEntryById(id, ignoreFilterFor(id))));
			const newValue = await loadDataTask;
			if (loadDataTask === this.loadDataTask)
				this.$emit('change', newValue);
		}
	}
};